import { Icon } from "@chakra-ui/react";
import { UserRound } from "lucide-react";

/**
 * What goes inside an account avatar circle: the customer's initials, or a person icon.
 *
 * ── WHY NOT "?" ──
 *
 * A question mark reads as an ERROR — "we don't know what this is" — and it sat on the
 * customer's own Account page, which made it faintly accusatory: they know who they are, and
 * being asked is odd. A person glyph is the ordinary "no avatar yet" convention and says
 * "your profile" rather than "something is missing".
 *
 * This stopped being an edge case when phone login was allowed to create an account without a
 * name (KAN-624). A nameless row is now the NORMAL state between signing in and making a first
 * booking, so the placeholder is something real customers see rather than a defensive fallback
 * nobody hits. The name is collected in the booking Details step and enforced server-side by
 * `assertNameForBooking`, so the gap closes on its own — which is exactly why the placeholder
 * should read as neutral and temporary rather than as a fault.
 *
 * ── SIZED IN `em`, NOT `rem` ──
 *
 * So it scales with whatever `fontSize` the surrounding circle already sets for its initials.
 * Four avatars render at four sizes; an absolute value would need threading through all of
 * them and would silently be wrong the next time one is resized.
 *
 * ── DECORATIVE ──
 *
 * `aria-hidden`, because every avatar in this app sits beside the display name in text. A
 * screen reader announcing "person icon" before that name is noise, and the icon carries no
 * information the name does not.
 */
export default function AvatarGlyph({ initials }: { initials: string }) {
  if (initials.trim()) return <>{initials}</>;
  return <Icon as={UserRound} boxSize="1.35em" aria-hidden strokeWidth={2.25} />;
}
